import React, { useState, useEffect, useRef } from 'react';
import Tesseract from 'tesseract.js';
import {
    FaCamera,
    FaFileUpload,
    FaPaste,
    FaSpinner,
    FaCheck,
    FaTimes,
    FaCheckSquare,
    FaSearch,
    FaExclamationTriangle,
    FaPlus,
    FaTrashAlt
} from 'react-icons/fa';

/**
 * Prétraitement d'image sur Canvas HTML5 pour optimiser la reconnaissance OCR
 * - Agrandissement (2.5x)
 * - Niveaux de gris
 * - Binarisation / Augmentation du contraste
 */
const preprocessImage = (imageElement) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scale = 2.5;
    canvas.width = Math.max(imageElement.naturalWidth || imageElement.width, 100) * scale;
    canvas.height = Math.max(imageElement.naturalHeight || imageElement.height, 100) * scale;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    // Convert to grayscale and apply contrast threshold
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;

        // Binarization with thresholding to make text super sharp
        const threshold = 150;
        const val = gray < threshold ? 0 : 255;

        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
    }

    ctx.putImageData(imgData, 0, 0);
    return canvas.toDataURL('image/png');
};

/**
 * Extraction des numéros de cas et rapprochement avec la liste réelle des dossiers
 */
export const extractCaseIdentifiers = (text, availableOrders = []) => {
    if (!text) return { matchedOrders: [], unmatchedNumbers: [] };

    const detectedNumbers = new Set();
    const cleanLines = text.split(/[\r\n,;|\t]+/);

    // Dictionnaire des numéros de cas existants normalisés pour matching ultra rapide
    // ex: "eno-69" -> order, "69" -> order, "069" -> order
    const orderNumberMap = new Map();
    const orderIdMap = new Map();

    availableOrders.forEach(order => {
        const fullCaseNum = (order.caseNumber || '').trim().toUpperCase();
        if (fullCaseNum) {
            orderNumberMap.set(fullCaseNum, order);
            const digits = fullCaseNum.replace(/\D/g, '');
            if (digits) {
                const intVal = parseInt(digits, 10);
                orderNumberMap.set(`ENO-${digits}`, order);
                orderNumberMap.set(`ENO-${intVal}`, order);
                orderNumberMap.set(`ENO-${intVal.toString().padStart(2, '0')}`, order);
                orderNumberMap.set(String(intVal), order);
            }
        }
        if (order.id) {
            orderIdMap.set(String(order.id).toLowerCase(), order);
            orderIdMap.set(String(order.id).substring(0, 8).toUpperCase(), order);
        }
        if (order.patientReference) {
            orderNumberMap.set(order.patientReference.trim().toUpperCase(), order);
        }
    });

    // 1. Recherche par Regex des motifs ENO (gérant les erreurs courantes d'OCR: EN0, END, ENG, B0, BO, BNO, etc.)
    const patterns = [
        /(?:ENO|EN0|END|ENG|BNO|B0|BO|EHO)[-\s_–—:]*([0-9]{1,6})/gi,
        /\b(?:ENO|EN0)[-\s_–—:]*([0-9A-Z]{1,6})\b/gi,
        /\b([1-9][0-9]{1,3})\b/g // Nombres isolés entre 10 et 9999
    ];

    patterns.forEach(regex => {
        let match;
        while ((match = regex.exec(text)) !== null) {
            const rawDigits = match[1] ? match[1].replace(/[^0-9]/g, '') : '';
            if (rawDigits) {
                const intVal = parseInt(rawDigits, 10);
                if (!isNaN(intVal) && intVal > 0) {
                    detectedNumbers.add(intVal);
                }
            }
        }
    });

    // 2. Parcourir chaque ligne découpée pour attraper les numéros purs
    cleanLines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;

        // Vérification si la ligne est un numéro pur (ex: 69, 73, 100)
        const digitsOnly = trimmed.replace(/\D/g, '');
        if (digitsOnly && digitsOnly.length >= 1 && digitsOnly.length <= 5) {
            const num = parseInt(digitsOnly, 10);
            if (!isNaN(num) && num > 0) {
                detectedNumbers.add(num);
            }
        }

        // Vérification directe par caseNumber ou ID
        const norm = trimmed.toUpperCase().replace(/\s+/g, '-');
        if (orderNumberMap.has(norm)) {
            const matched = orderNumberMap.get(norm);
            const digits = (matched.caseNumber || '').replace(/\D/g, '');
            if (digits) detectedNumbers.add(parseInt(digits, 10));
        }
    });

    // Rapprochement avec les commandes réelles
    const matchedOrders = [];
    const matchedOrderIds = new Set();
    const unmatchedNumbers = [];

    detectedNumbers.forEach(num => {
        // Trouver la commande correspondante
        const numStr = String(num);
        const enoPadded = `ENO-${numStr.padStart(2, '0')}`;
        const enoDirect = `ENO-${numStr}`;

        let matched = orderNumberMap.get(enoPadded) || orderNumberMap.get(enoDirect) || orderNumberMap.get(numStr);

        // Si non trouvé dans la map, recherche dans availableOrders par regex
        if (!matched) {
            matched = availableOrders.find(o => {
                const cDigits = (o.caseNumber || o.id || '').replace(/\D/g, '');
                return cDigits && parseInt(cDigits, 10) === num;
            });
        }

        if (matched && !matchedOrderIds.has(matched.id)) {
            matchedOrders.push(matched);
            matchedOrderIds.add(matched.id);
        } else if (!matched) {
            unmatchedNumbers.push(`ENO-${num}`);
        }
    });

    // Tri par numéro de cas croissant
    matchedOrders.sort((a, b) => {
        const numA = parseInt((a.caseNumber || '').replace(/\D/g, '') || '0', 10);
        const numB = parseInt((b.caseNumber || '').replace(/\D/g, '') || '0', 10);
        return numA - numB;
    });

    return {
        matchedOrders,
        unmatchedNumbers
    };
};

const CaseListScannerModal = ({
    isOpen,
    onClose,
    orders = [],
    onApplySelection,
    onBulkStatusChange,
    onOpenBulkDocuments
}) => {
    const [activeTab, setActiveTab] = useState('image'); // 'image' ou 'text'
    const [imagePreview, setImagePreview] = useState(null);
    const [manualText, setManualText] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [matchedCases, setMatchedCases] = useState([]);
    const [unmatchedCases, setUnmatchedCases] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [manualAddInput, setManualAddInput] = useState('');
    const fileInputRef = useRef(null);

    const processImage = React.useCallback(async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            alert('Veuillez sélectionner ou coller une image valide (.png, .jpg, .webp).');
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        setLoading(true);
        setStatusText("Prétraitement de l'image (Canvas & Contraste)...");

        try {
            // 1. Créer une balise image temporaire pour le prétraitement Canvas
            const img = new Image();
            img.src = previewUrl;
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            const processedDataUrl = preprocessImage(img);

            setStatusText("Lecture OCR des numéros de cas (Tesseract)...");
            const worker = await Tesseract.createWorker('eng+fra');
            await worker.setParameters({
                tessedit_pageseg_mode: Tesseract.PSM.SINGLE_COLUMN,
                tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_ /.:\n\r'
            });

            const ret = await worker.recognize(processedDataUrl);
            const rawText = ret.data.text;
            await worker.terminate();

            setStatusText("Rapprochement avec vos dossiers...");
            const { matchedOrders, unmatchedNumbers } = extractCaseIdentifiers(rawText, orders);

            setMatchedCases(matchedOrders);
            setUnmatchedCases(unmatchedNumbers);
            setLoading(false);
            setStatusText("");
        } catch (error) {
            console.error('Erreur OCR:', error);
            setLoading(false);
            setStatusText("");
            alert("Erreur lors de la lecture de l'image. Vous pouvez également coller les numéros dans l'onglet Texte.");
        }
    }, [orders]);

    // Initialiser la sélection lorsque les cas matchés changent
    useEffect(() => {
        setSelectedOrderIds(matchedCases.map(c => c.id));
    }, [matchedCases]);

    // Écoute de l'événement de collage (Ctrl+V) au niveau de la fenêtre
    useEffect(() => {
        if (!isOpen) return;

        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    processImage(blob);
                    e.preventDefault();
                    return;
                }
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [isOpen, processImage]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            processImage(e.target.files[0]);
        }
    };

    const handleParseManualText = () => {
        if (!manualText.trim()) return;
        setLoading(true);
        const { matchedOrders, unmatchedNumbers } = extractCaseIdentifiers(manualText, orders);
        setMatchedCases(matchedOrders);
        setUnmatchedCases(unmatchedNumbers);
        setLoading(false);
    };

    const toggleSelectOrder = (id) => {
        setSelectedOrderIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedOrderIds.length === matchedCases.length) {
            setSelectedOrderIds([]);
        } else {
            setSelectedOrderIds(matchedCases.map(c => c.id));
        }
    };

    const handleAddManualCase = () => {
        if (!manualAddInput.trim()) return;
        const inputVal = manualAddInput.trim();
        const { matchedOrders } = extractCaseIdentifiers(inputVal, orders);

        if (matchedOrders.length > 0) {
            const newMatches = [...matchedCases];
            matchedOrders.forEach(o => {
                if (!newMatches.some(m => m.id === o.id)) {
                    newMatches.push(o);
                }
            });
            setMatchedCases(newMatches);
            setSelectedOrderIds(prev => Array.from(new Set([...prev, ...matchedOrders.map(o => o.id)])));
            setManualAddInput('');
        } else {
            if (!unmatchedCases.includes(inputVal)) {
                setUnmatchedCases(prev => [...prev, inputVal]);
            }
            setManualAddInput('');
        }
    };

    const handleRemoveCase = (id) => {
        setMatchedCases(prev => prev.filter(c => c.id !== id));
        setSelectedOrderIds(prev => prev.filter(i => i !== id));
    };

    const handleRemoveUnmatched = (val) => {
        setUnmatchedCases(prev => prev.filter(u => u !== val));
    };

    const handleApplySelection = () => {
        if (onApplySelection) {
            onApplySelection(selectedOrderIds);
            onClose();
        }
    };

    const handleDirectStatusChange = async (status) => {
        if (!status || selectedOrderIds.length === 0) return;
        if (onBulkStatusChange) {
            await onBulkStatusChange(status, selectedOrderIds);
            onClose();
        }
    };

    const handleDirectDocGeneration = (docType) => {
        if (!docType || selectedOrderIds.length === 0) return;
        if (onOpenBulkDocuments) {
            onOpenBulkDocuments(docType, selectedOrderIds);
            onClose();
        }
    };

    const getStatusBadge = (status) => {
        const s = (status || '').toLowerCase();
        let bg = '#e2e8f0';
        let color = '#334155';
        if (s === 'reçu' || s === 'recu') { bg = '#dbeafe'; color = '#1e40af'; }
        else if (s === 'production' || s === 'en cours') { bg = '#fef3c7'; color = '#92400e'; }
        else if (s === 'livré' || s === 'livre') { bg = '#dcfce7'; color = '#15803d'; }
        else if (s === 'facturé' || s === 'facture') { bg = '#f3e8ff'; color = '#6b21a8'; }
        else if (s === 'en attente') { bg = '#fee2e2'; color = '#b91c1c'; }

        return (
            <span
                style={{
                    backgroundColor: bg,
                    color: color,
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                }}
            >
                {status || 'En cours'}
            </span>
        );
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.85)',
                backdropFilter: 'blur(8px)',
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '20px',
                    width: '100%',
                    maxWidth: '1000px',
                    maxHeight: '92vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    animation: 'fadeIn 0.2s ease-out'
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '18px 24px',
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white"
                            style={{
                                width: '44px',
                                height: '44px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            <FaCamera size={22} />
                        </div>
                        <div>
                            <h5 className="m-0 fw-bold" style={{ color: '#ffffff', fontSize: '18px' }}>
                                Scan List &mdash; Sélection Intelligente de Cas
                            </h5>
                            <small style={{ color: '#bfdbfe', fontSize: '13px' }}>
                                Collez une capture de listing fournisseur (Ctrl+V) ou une liste de numéros pour tout sélectionner d'un coup
                            </small>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                    />
                </div>

                {/* Body */}
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff', color: '#0f172a' }}>
                        {/* Tabs */}
                        <div className="d-flex justify-content-center gap-2 mb-4">
                            <button
                                type="button"
                                className="btn fw-bold px-4 py-2"
                                onClick={() => setActiveTab('image')}
                                style={{
                                    backgroundColor: activeTab === 'image' ? '#2563eb' : '#f1f5f9',
                                    color: activeTab === 'image' ? '#ffffff' : '#475569',
                                    borderRadius: '10px',
                                    border: 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FaFileUpload className="me-2" /> Capture d'écran (Image / Ctrl+V)
                            </button>
                            <button
                                type="button"
                                className="btn fw-bold px-4 py-2"
                                onClick={() => setActiveTab('text')}
                                style={{
                                    backgroundColor: activeTab === 'text' ? '#2563eb' : '#f1f5f9',
                                    color: activeTab === 'text' ? '#ffffff' : '#475569',
                                    borderRadius: '10px',
                                    border: 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FaPaste className="me-2" /> Copier / Coller Liste Texte
                            </button>
                        </div>

                        {/* Mode Image */}
                        {activeTab === 'image' && (
                            <div
                                className="rounded-4 p-4 text-center mb-4 position-relative"
                                style={{
                                    backgroundColor: '#f8fafc',
                                    border: '2px dashed #3b82f6',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                        processImage(e.dataTransfer.files[0]);
                                    }
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <div className="my-2">
                                    <div
                                        className="d-inline-flex p-3 rounded-circle mb-2"
                                        style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
                                    >
                                        <FaCamera size={30} />
                                    </div>
                                    <h6 className="fw-bold mb-1" style={{ color: '#0f172a', fontSize: '16px' }}>
                                        Glissez la capture d'écran ici ou cliquez pour choisir
                                    </h6>
                                    <p className="mb-2" style={{ color: '#64748b', fontSize: '13px' }}>
                                        Ou appuyez simplement sur <kbd style={{ background: '#e2e8f0', color: '#0f172a', padding: '2px 6px', borderRadius: '4px' }}>Ctrl + V</kbd> n'importe où
                                    </p>
                                    <span
                                        className="badge fw-semibold"
                                        style={{
                                            backgroundColor: '#eff6ff',
                                            color: '#1d4ed8',
                                            border: '1px solid #bfdbfe',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        Reconnaissance automatique des colonnes & tableaux (ENO-69, ENO-73...)
                                    </span>
                                </div>

                                {imagePreview && (
                                    <div className="mt-3 p-2 bg-white rounded-3 border d-inline-block shadow-sm">
                                        <img
                                            src={imagePreview}
                                            alt="Aperçu listing"
                                            style={{ maxHeight: '130px', maxWidth: '100%', objectFit: 'contain', borderRadius: '6px' }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Mode Texte */}
                        {activeTab === 'text' && (
                            <div className="mb-4">
                                <label className="form-label fw-bold small text-secondary">
                                    Collez votre liste de numéros de cas (séparés par retours à la ligne, virgules ou depuis un fichier Excel) :
                                </label>
                                <textarea
                                    className="form-control p-3"
                                    rows="5"
                                    placeholder="Exemple :&#10;ENO-69&#10;ENO-73&#10;ENO-74&#10;ENO-79&#10;ENO-77&#10;ENO-75..."
                                    value={manualText}
                                    onChange={(e) => setManualText(e.target.value)}
                                    style={{
                                        borderRadius: '12px',
                                        borderColor: '#cbd5e1',
                                        fontSize: '14px',
                                        fontFamily: 'monospace'
                                    }}
                                />
                                <div className="mt-2 text-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary fw-semibold px-4 py-2"
                                        onClick={handleParseManualText}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        <FaSearch className="me-2" /> Analyser la liste
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div className="text-center py-4 bg-light rounded-3 border mb-4">
                                <FaSpinner className="spinner-border text-primary mb-2" style={{ width: '2.2rem', height: '2.2rem' }} />
                                <p className="fw-bold mb-0 text-dark">{statusText || 'Analyse en cours...'}</p>
                            </div>
                        )}

                        {/* Results Section */}
                        {(matchedCases.length > 0 || unmatchedCases.length > 0) && !loading && (
                            <div className="results-container">
                                {/* Header summary */}
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 p-3 rounded-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <FaCheck className="text-success" size={18} />
                                        <span className="fw-bold text-success" style={{ fontSize: '15px' }}>
                                            {matchedCases.length} cas détecté{matchedCases.length > 1 ? 's' : ''} et identifié{matchedCases.length > 1 ? 's' : ''} dans la base
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success fw-bold"
                                            onClick={toggleSelectAll}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            {selectedOrderIds.length === matchedCases.length ? 'Tout décocher' : 'Tout cocher'} ({selectedOrderIds.length}/{matchedCases.length})
                                        </button>
                                    </div>
                                </div>

                                {/* Manual Add Input */}
                                <div className="input-group mb-3" style={{ maxWidth: '400px' }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ajouter un cas manuellement (ex: ENO-80)..."
                                        value={manualAddInput}
                                        onChange={(e) => setManualAddInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddManualCase(); }}
                                        style={{ borderRadius: '8px 0 0 8px' }}
                                    />
                                    <button
                                        className="btn btn-outline-primary fw-semibold"
                                        type="button"
                                        onClick={handleAddManualCase}
                                        style={{ borderRadius: '0 8px 8px 0' }}
                                    >
                                        <FaPlus className="me-1" /> Ajouter
                                    </button>
                                </div>

                                {/* Table of matched cases */}
                                {matchedCases.length > 0 && (
                                    <div className="table-responsive border rounded-3 mb-3" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                        <table className="table table-hover align-middle mb-0" style={{ fontSize: '13.5px' }}>
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: '40px' }} className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedOrderIds.length === matchedCases.length && matchedCases.length > 0}
                                                            onChange={toggleSelectAll}
                                                        />
                                                    </th>
                                                    <th>N° Dossier</th>
                                                    <th>Patient</th>
                                                    <th>Praticien</th>
                                                    <th>Date de Livraison</th>
                                                    <th>Statut Actuel</th>
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matchedCases.map(order => {
                                                    const isChecked = selectedOrderIds.includes(order.id);
                                                    return (
                                                        <tr
                                                            key={order.id}
                                                            style={{
                                                                backgroundColor: isChecked ? '#f0fdf4' : 'transparent',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => toggleSelectOrder(order.id)}
                                                        >
                                                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => toggleSelectOrder(order.id)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <strong className="text-primary font-monospace" style={{ fontSize: '14px' }}>
                                                                    {order.caseNumber || `#${order.id.substring(0, 8)}`}
                                                                </strong>
                                                            </td>
                                                            <td>
                                                                <strong className="text-dark">{order.patientName?.toUpperCase() || '-'}</strong>
                                                                {order.patientReference && (
                                                                    <span className="ms-1 badge bg-light text-secondary border fw-normal" style={{ fontSize: '10px' }}>
                                                                        {order.patientReference}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-muted">Dr. {order.practitionerName || '-'}</td>
                                                            <td>
                                                                {order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString('fr-FR') : '-'}
                                                            </td>
                                                            <td>{getStatusBadge(order.status)}</td>
                                                            <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-danger p-0"
                                                                    onClick={() => handleRemoveCase(order.id)}
                                                                    title="Retirer de la liste"
                                                                >
                                                                    <FaTrashAlt size={13} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Unmatched cases alert if any */}
                                {unmatchedCases.length > 0 && (
                                    <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <FaExclamationTriangle className="text-warning flex-shrink-0" />
                                            <strong style={{ color: '#92400e', fontSize: '13px' }}>
                                                {unmatchedCases.length} référence(s) détectée(s) non trouvée(s) dans votre base :
                                            </strong>
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                            {unmatchedCases.map(item => (
                                                <span
                                                    key={item}
                                                    className="badge bg-white text-secondary border d-inline-flex align-items-center gap-1 font-monospace"
                                                    style={{ fontSize: '12px', padding: '5px 8px' }}
                                                >
                                                    {item}
                                                    <FaTimes
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleRemoveUnmatched(item)}
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer with instant bulk actions */}
                    <div
                        className="modal-footer p-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
                        style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
                    >
                        <div>
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-semibold px-4"
                                onClick={onClose}
                                style={{ borderRadius: '10px' }}
                            >
                                Fermer
                            </button>
                        </div>

                        {selectedOrderIds.length > 0 && (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {/* Option 1: Direct Status Change */}
                                <select
                                    className="form-select form-select-sm fw-semibold bg-dark text-white border-0 px-3 py-2"
                                    style={{ borderRadius: '10px', minWidth: '180px', cursor: 'pointer' }}
                                    defaultValue=""
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            handleDirectStatusChange(e.target.value);
                                            e.target.value = '';
                                        }
                                    }}
                                >
                                    <option value="" disabled>⚡ Passer le statut à...</option>
                                    <option value="Livré">🚚 Livré ({selectedOrderIds.length})</option>
                                    <option value="Facturé">💶 Facturé ({selectedOrderIds.length})</option>
                                    <option value="Production">⚙️ Production ({selectedOrderIds.length})</option>
                                    <option value="En attente">⏳ En attente ({selectedOrderIds.length})</option>
                                    <option value="Reçu">📥 Reçu ({selectedOrderIds.length})</option>
                                </select>

                                {/* Option 2: Direct Document Generation */}
                                <select
                                    className="form-select form-select-sm fw-bold bg-success text-white border-0 px-3 py-2"
                                    style={{ borderRadius: '10px', minWidth: '200px', cursor: 'pointer' }}
                                    defaultValue=""
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            handleDirectDocGeneration(e.target.value);
                                            e.target.value = '';
                                        }
                                    }}
                                >
                                    <option value="" disabled>🖨️ Générer documents...</option>
                                    <option value="BL">🚚 Bons de Livraison BL ({selectedOrderIds.length})</option>
                                    <option value="WORKSHEET">📋 Fiches de Travail ({selectedOrderIds.length})</option>
                                    <option value="PATIENT">👤 Fiches Patient ({selectedOrderIds.length})</option>
                                    <option value="QUOTE">📄 Devis ({selectedOrderIds.length})</option>
                                    <option value="INVOICE">💶 Factures ({selectedOrderIds.length})</option>
                                </select>

                                {/* Option 3: Apply selection in main table */}
                                <button
                                    type="button"
                                    className="btn btn-primary fw-bold px-4 py-2 d-inline-flex align-items-center gap-2"
                                    onClick={handleApplySelection}
                                    style={{
                                        borderRadius: '10px',
                                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                                    }}
                                >
                                    <FaCheckSquare /> Sélectionner dans Case Box ({selectedOrderIds.length})
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
    );
};

export default CaseListScannerModal;
